/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import cn from 'classnames';
import { useAtomValue } from 'jotai';

import { generateSrcSet } from '@core/Components/HeycarImage/GenerateSrcSet';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { Typography } from '@gds/Typography/Typography';
import { RichText } from '@page-builder/Components/RichText/RichText';
import { getAbsoluteMediaUrl } from '@umbraco/Utils/GetAbsoluteMediaUrl';
import { umbracoImageLoader } from '@umbraco/Utils/UmbracoImageLoader';

import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { eligibleProductsAtom } from '../../../Atoms/EligibleProducts.atom';
import type {
  FinanceHelpModalContent,
  FinanceImageLabelOptions,
  FinanceItem,
} from '../../../Entities/FinanceHelpModal.entity';
import { CAPTIVE_MAP } from '../Helpers/FinanceMap';
import { financeHelpConfig, guideUrl, videoUrl } from './FinanceHelpModal.constants';

import styles from './FinanceHelpModal.module.css';

type FinanceModalStrings = {
  paymentPlan: string;
  dealership: string;
  pcpFilm?: string;
  pcpGuide?: string;
  hpFilm?: string;
  hpGuide?: string;
  downPayment: string;
  monthlyInstallments: string;
  closingOptions: string;
};
interface FinanceHelpModalProps {
  locale: Locale;
  financeMessages: FinanceHelpModalContent;
  financeModalStrings: FinanceModalStrings;
  vehicle: Vehicle;
}

export const FinanceHelpModal = ({
  financeMessages,
  financeModalStrings,
  locale,
  vehicle,
}: FinanceHelpModalProps) => {
  const eligibleProducts = useAtomValue(eligibleProductsAtom)!;
  const calculation = useAtomValue(calculationAtom)!;
  const config = financeHelpConfig[locale];
  const financeMessagesForVehicle = getFinanceMessagesForVehicle({
    vehicle,
    financeMessages,
    eligibleProducts,
    captiveId: calculation.captiveId,
  });

  return (
    <div className={styles.financeModal}>
      <div className={styles.financeModalDescription}>
        <RichText noPadding richText={financeMessagesForVehicle.heading} />
      </div>
      <div className={styles.financeModalContent}>
        <FinanceOptions
          financeMessagesForVehicle={financeMessagesForVehicle}
          financeModalStrings={financeModalStrings}
          {...config}
        />
      </div>
      {config.showDealership && (
        <div className={styles.financeModalFooter}>
          <Typography variant="caption2" Component="span">
            {financeModalStrings.dealership}
          </Typography>
        </div>
      )}
    </div>
  );
};

function FinanceOptions({
  financeMessagesForVehicle,
  showFinanceUsp,
  showPaymentPlanText,
  showPaymentPlanImage,
  showPcpGuide,
  showHPGuide,
  financeModalStrings,
}: {
  financeMessagesForVehicle: FinanceHelpModalContent;
  financeModalStrings: FinanceModalStrings;
  showFinanceUsp: boolean;
  showPaymentPlanText: boolean;
  showPaymentPlanImage: boolean;
  showPcpGuide: boolean;
  showHPGuide: boolean;
}) {
  const { financeProducts } = financeMessagesForVehicle;

  return financeProducts.map(financeOption => {
    const bodyImageUrl = getAbsoluteMediaUrl(financeOption.bodyImage[0]?.url);
    return (
      <div
        className={cn(styles.financeModalOption, { [styles.wide]: financeProducts.length < 3 })}
        key={financeOption.financeType}
      >
        <div className={styles.financeModalOptionTitle}>
          <Typography variant="h5" Component="span">
            {financeOption.subheader}
          </Typography>
        </div>
        <div className={styles.financeModalOptionSubtitle}>
          <Typography variant="body3" Component="span">
            {financeOption.description}
          </Typography>
        </div>
        {showFinanceUsp && (
          <ul className={styles.financeModalUspsWrapper}>
            <UspItems uspItems={financeOption.financeItems} />
          </ul>
        )}
        {(showPaymentPlanText || showPaymentPlanImage) && (
          <div className={styles.financeModalPaymentPlan}>
            {showPaymentPlanImage && (
              <>
                <Typography variant="subheading3">{financeModalStrings.paymentPlan}</Typography>

                <div className={styles.financeModalImage}>
                  <ImageLabels
                    labels={financeOption.bodyImageLabels}
                    strings={financeModalStrings}
                  />
                  {bodyImageUrl && (
                    <img
                      src={bodyImageUrl}
                      srcSet={generateSrcSet(bodyImageUrl, umbracoImageLoader)}
                      sizes="365px"
                      alt=""
                    />
                  )}
                </div>
              </>
            )}
            {showPaymentPlanText && (
              <>
                <Typography variant="subheading3">{financeOption.bodyTitle}</Typography>
                <ul className={styles.financeModalUspsWrapper}>
                  <UspItems uspItems={financeOption.bodyItems} />
                </ul>
              </>
            )}
          </div>
        )}
        {showPcpGuide && financeOption.financeType === 'PCP' && (
          <div className={styles.financeModalLinksFooter}>
            <a
              href={videoUrl.pcp}
              className={styles.financeModalGuideLinks}
              target="_blank"
              rel="noreferrer"
            >
              <Typography variant="body2" Component="span">
                {financeModalStrings.pcpFilm}
              </Typography>
            </a>
            <a
              href={guideUrl.pcp}
              className={styles.financeModalGuideLinks}
              target="_blank"
              rel="noreferrer"
            >
              <Typography variant="body2" Component="span">
                {financeModalStrings.pcpGuide}
              </Typography>
            </a>
          </div>
        )}

        {showHPGuide && financeOption.financeType === 'HP' && (
          <div className={styles.financeModalLinksFooter}>
            <a
              href={videoUrl.hp}
              className={styles.financeModalGuideLinks}
              target="_blank"
              rel="noreferrer"
            >
              <Typography variant="body2" Component="span">
                {financeModalStrings.hpFilm}
              </Typography>
            </a>
            <a
              href={guideUrl.hp}
              className={styles.financeModalGuideLinks}
              target="_blank"
              rel="noreferrer"
            >
              <Typography variant="body2" Component="span">
                {financeModalStrings.hpGuide}
              </Typography>
            </a>
          </div>
        )}
      </div>
    );
  });
}

function UspItems({ uspItems }: { uspItems: FinanceItem[] }) {
  return uspItems.map(item => {
    const iconUrl = getAbsoluteMediaUrl(item.icon[0]?.url);
    return (
      <li className={styles.uspItem} key={item.title}>
        {iconUrl && (
          <div className={styles.uspIcon}>
            <img
              src={iconUrl}
              srcSet={generateSrcSet(iconUrl, umbracoImageLoader, [20, 40])}
              sizes="20px"
              alt=""
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
        <div>{item.title}</div>
      </li>
    );
  });
}

function ImageLabels({
  labels,
  strings,
}: {
  labels: FinanceImageLabelOptions;
  strings: FinanceModalStrings;
}) {
  return (
    <ul className={styles.imageHelpers}>
      {labels.map(option => (
        <li key={option.codename}>{strings[option.name]}</li>
      ))}
    </ul>
  );
}

const PRODUCT_ID_MAPPING = {
  privateLeasing: {
    [CAPTIVE_MAP.PFS]: 'privateLeasingPFS',
  },
};

function getFinanceMessagesForVehicle({
  vehicle,
  financeMessages,
  eligibleProducts,
  captiveId,
}: {
  vehicle: Vehicle;
  financeMessages: FinanceHelpModalContent;
  eligibleProducts: string[];
  captiveId: string;
}): FinanceHelpModalContent {
  if (!vehicle.pricing.hasFinance) {
    return {
      heading: '',
      financeProducts: [],
    };
  }

  const productsForCaptiveId = eligibleProducts.map(productId => {
    return (
      PRODUCT_ID_MAPPING[productId as keyof typeof PRODUCT_ID_MAPPING]?.[captiveId] ?? productId
    );
  });

  const financeProducts = financeMessages.financeProducts.filter(financeProduct => {
    return productsForCaptiveId.includes(financeProduct.financeType);
  });

  return {
    heading: financeMessages.heading,
    financeProducts,
  };
}
